import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Briefcase,
  MapPin,
  Star,
  Building2,
  Search,
} from "lucide-react";
import { api } from "../lib/api";
import EmployerLogo from "../components/EmployerLogo";
import { useSeo } from "../lib/useSeo";

const SITE_URL =
  "https://railwaycareers.co.uk";

const normaliseEmployerName = (
  value = ""
) =>
  value
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(
      /[.,'’"]/g,
      ""
    )
    .replace(
      /\s+/g,
      " "
    )
    .trim();

const employerRoute = (
  employer
) => {
  if (
    employer
      ?.featured_profile
      ?.profile_slug
  ) {
    return `/companies/${employer.featured_profile.profile_slug}`;
  }

  return `/operators/${employer.slug}`;
};

export default function Operators() {
  const [
    operators,
    setOperators,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    query,
    setQuery,
  ] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadEmployers =
      async () => {
        try {
          const [
            operatorsResponse,
            featuredResponse,
          ] =
            await Promise.all(
              [
                api.get(
                  "/operators"
                ),

                api
                  .get(
                    "/featured-employers"
                  )
                  .catch(
                    () => ({
                      data: [],
                    })
                  ),
              ]
            );

          if (!mounted) {
            return;
          }

          const operatorData =
            Array.isArray(
              operatorsResponse.data
            )
              ? operatorsResponse.data
              : [];

          const featuredData =
            Array.isArray(
              featuredResponse.data
            )
              ? featuredResponse.data
              : [];

          const featuredByName =
            new Map();

          featuredData.forEach(
            (
              profile
            ) => {
              if (
                !profile?.company_name
              ) {
                return;
              }

              featuredByName.set(
                normaliseEmployerName(
                  profile.company_name
                ),
                profile
              );
            }
          );

          const combined =
            operatorData
              .filter(
                (
                  operator
                ) =>
                  operator?.name &&
                  operator?.slug
              )
              .map(
                (
                  operator
                ) => {
                  const featuredProfile =
                    featuredByName.get(
                      normaliseEmployerName(
                        operator.name
                      )
                    ) ||
                    null;

                  return {
                    ...operator,

                    count:
                      Number(
                        operator.count ||
                          0
                      ),

                    featured_count:
                      Number(
                        operator.featured_count ||
                          0
                      ),

                    regions:
                      Array.isArray(
                        operator.regions
                      )
                        ? operator.regions
                        : [],

                    featured_employer:
                      Boolean(
                        featuredProfile
                      ),

                    featured_profile:
                      featuredProfile,
                  };
                }
              );

          /*
           * Featured Employers retain directory visibility even when they
           * have no live vacancy.
           */
          featuredData.forEach(
            (
              profile
            ) => {
              if (
                !profile?.company_name ||
                !profile?.profile_slug
              ) {
                return;
              }

              const alreadyExists =
                combined.some(
                  (
                    operator
                  ) =>
                    normaliseEmployerName(
                      operator.name
                    ) ===
                    normaliseEmployerName(
                      profile.company_name
                    )
                );

              if (
                alreadyExists
              ) {
                return;
              }

              combined.push(
                {
                  name:
                    profile.company_name,

                  slug:
                    profile.profile_slug,

                  count: 0,

                  featured_count: 0,

                  regions: [],

                  featured_employer:
                    true,

                  featured_profile:
                    profile,
                }
              );
            }
          );

          setOperators(
            combined
          );
        } catch {
          if (mounted) {
            setOperators([]);
          }
        } finally {
          if (mounted) {
            setLoading(false);
          }
        }
      };

    loadEmployers();

    return () => {
      mounted = false;
    };
  }, []);

  const sortedOperators =
    useMemo(() => {
      return [
        ...operators,
      ].sort(
        (
          a,
          b
        ) => {
          if (
            Boolean(
              a.featured_employer
            ) !==
            Boolean(
              b.featured_employer
            )
          ) {
            return a.featured_employer
              ? -1
              : 1;
          }

          if (
            (b.count ||
              0) !==
            (a.count ||
              0)
          ) {
            return (
              (b.count ||
                0) -
              (a.count ||
                0)
            );
          }

          return a.name.localeCompare(
            b.name
          );
        }
      );
    }, [operators]);

  const filteredOperators =
    useMemo(() => {
      const searchTerm =
        query
          .trim()
          .toLowerCase();

      if (
        !searchTerm
      ) {
        return sortedOperators;
      }

      return sortedOperators.filter(
        (
          operator
        ) => {
          const nameMatch =
            operator.name
              .toLowerCase()
              .includes(
                searchTerm
              );

          const regionMatch =
            operator.regions.some(
              (
                region
              ) =>
                region
                  .toLowerCase()
                  .includes(
                    searchTerm
                  )
            );

          const taglineMatch =
            (
              operator
                .featured_profile
                ?.profile_tagline ||
              ""
            )
              .toLowerCase()
              .includes(
                searchTerm
              );

          return (
            nameMatch ||
            regionMatch ||
            taglineMatch
          );
        }
      );
    }, [
      query,
      sortedOperators,
    ]);

  const totalVacancies =
    useMemo(() => {
      return operators.reduce(
        (
          acc,
          operator
        ) =>
          acc +
          (operator.count ||
            0),
        0
      );
    }, [operators]);

  const featuredEmployerCount =
    useMemo(() => {
      return operators.filter(
        (
          operator
        ) =>
          operator.featured_employer
      ).length;
    }, [operators]);

  useSeo({
    title:
      "UK Rail Employers Hiring Now | Railway Careers UK",

    description:
      "Browse UK Train Operating Companies, freight operators, infrastructure employers, contractors and railway suppliers with live UK rail vacancies and employer profiles.",

    canonical:
      "/operators",

    jsonLd: {
      "@context":
        "https://schema.org",

      "@graph": [
        {
          "@type":
            "WebPage",

          "@id":
            `${SITE_URL}/operators`,

          url:
            `${SITE_URL}/operators`,

          name:
            "UK Rail Employers Hiring Now",

          description:
            "A live directory of UK rail employers, Train Operating Companies, freight operators, infrastructure managers and major railway contractors.",

          isPartOf: {
            "@type":
              "WebSite",

            name:
              "Railway Careers UK",

            url:
              SITE_URL,
          },
        },

        {
          "@type":
            "BreadcrumbList",

          itemListElement:
            [
              {
                "@type":
                  "ListItem",

                position:
                  1,

                name:
                  "Home",

                item:
                  `${SITE_URL}/`,
              },

              {
                "@type":
                  "ListItem",

                position:
                  2,

                name:
                  "Rail Employers",

                item:
                  `${SITE_URL}/operators`,
              },
            ],
        },

        {
          "@type":
            "ItemList",

          name:
            "UK Rail Employers",

          itemListElement:
            sortedOperators
              .slice(
                0,
                50
              )
              .map(
                (
                  operator,
                  index
                ) => ({
                  "@type":
                    "ListItem",

                  position:
                    index +
                    1,

                  url:
                    `${SITE_URL}${employerRoute(
                      operator
                    )}`,

                  name:
                    operator.name,
                })
              ),
        },
      ],
    },
  });

  return (
    <div className="bg-nav min-h-screen">
      <header className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-12">
        <div className="chapter-mark">
          UK Rail Employers
        </div>

        <h1 className="mt-6 font-display text-ice text-4xl md:text-6xl tracking-[-0.025em] font-extrabold leading-[1.05] max-w-4xl">
          UK rail employers{" "}
          <span className="text-sky">
            hiring right now.
          </span>
        </h1>

        <p className="mt-5 text-slate text-lg max-w-2xl leading-relaxed">
          Browse Train Operating Companies, freight operators,
          infrastructure organisations, contractors and rail suppliers with
          live vacancies and employer profiles on Railway Careers UK.
        </p>

        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-slate font-mono">
          <span className="inline-flex items-center gap-1.5 text-sky">
            <Building2 className="w-4 h-4" />
            {operators.length} employers
          </span>

          <span className="inline-flex items-center gap-1.5">
            <Briefcase className="w-4 h-4 text-sky" />
            {totalVacancies} live vacancies
          </span>

          {featuredEmployerCount >
            0 && (
            <span className="inline-flex items-center gap-1.5 text-gold">
              <Star className="w-4 h-4 fill-current" />

              {featuredEmployerCount} featured{" "}
              {featuredEmployerCount ===
              1
                ? "employer"
                : "employers"}
            </span>
          )}
        </div>

        <div className="mt-8 max-w-xl relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate" />

          <input
            type="search"
            value={query}
            onChange={(
              event
            ) =>
              setQuery(
                event.target
                  .value
              )
            }
            placeholder="Search by employer or region..."
            className="w-full rounded-xl border border-line bg-deep text-ice placeholder:text-slate px-11 py-3 outline-none focus:border-sky"
            data-testid="operators-search"
          />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({
              length:
                6,
            }).map(
              (
                _,
                index
              ) => (
                <div
                  key={
                    index
                  }
                  className="paper-card p-6 h-36 animate-pulse"
                />
              )
            )}
          </div>
        ) : operators.length ===
          0 ? (
          <div className="paper-card p-10 text-center">
            <h2 className="font-display text-ice text-2xl font-bold">
              No employers found yet
            </h2>

            <p className="mt-3 text-slate">
              Employer listings will appear here as vacancies and company
              profiles are published.
            </p>
          </div>
        ) : filteredOperators.length ===
          0 ? (
          <div className="paper-card p-10 text-center">
            <h2 className="font-display text-ice text-2xl font-bold">
              No matching employers
            </h2>

            <p className="mt-3 text-slate">
              Try searching for another employer name or region.
            </p>
          </div>
        ) : (
          <>
            {featuredEmployerCount >
              0 &&
              !query.trim() && (
                <div className="mb-5 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-gold font-mono">
                  <Star className="w-3.5 h-3.5 fill-current" />
                  Featured employers appear first
                </div>
              )}

            <div
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
              data-testid="operators-grid"
            >
              {filteredOperators.map(
                (
                  operator,
                  index
                ) => {
                  const featuredProfile =
                    operator.featured_profile;

                  const featuredEmployer =
                    Boolean(
                      operator.featured_employer
                    );

                  const route =
                    employerRoute(
                      operator
                    );

                  const logoOverride =
                    featuredProfile?.profile_logo_url ||
                    undefined;

                  const tagline =
                    featuredProfile?.profile_tagline ||
                    "";

                  return (
                    <motion.div
                      key={`${operator.slug}-${operator.name}`}
                      initial={{
                        opacity:
                          0,
                        y: 14,
                      }}
                      whileInView={{
                        opacity:
                          1,
                        y: 0,
                      }}
                      viewport={{
                        once: true,
                        margin:
                          "-50px",
                      }}
                      transition={{
                        duration:
                          0.45,

                        delay:
                          Math.min(
                            index *
                              0.035,
                            0.35
                          ),
                      }}
                    >
                      <Link
                        to={
                          route
                        }
                        data-testid={`operator-card-${operator.slug}`}
                        className={`group block paper-card p-6 h-full transition-all hover:-translate-y-0.5 ${
                          featuredEmployer
                            ? "!border-sky/60 ring-1 ring-sky/10"
                            : ""
                        }`}
                      >
                        {featuredEmployer && (
                          <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-sky/30 bg-sky/10 px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-bold text-sky">
                            <Star className="w-3 h-3 fill-current" />
                            Featured employer
                          </div>
                        )}

                        <div className="flex items-start gap-4">
                          <EmployerLogo
                            name={
                              operator.name
                            }
                            override={
                              logoOverride
                            }
                            size={
                              56
                            }
                            className="shrink-0"
                          />

                          <div className="flex-1 min-w-0">
                            <h2 className="font-display text-ice text-xl font-bold tracking-[-0.012em] leading-tight">
                              {
                                operator.name
                              }
                            </h2>

                            {tagline ? (
                              <div className="mt-1.5 text-[12px] text-slate leading-relaxed line-clamp-2">
                                {
                                  tagline
                                }
                              </div>
                            ) : (
                              <div className="mt-1 text-[12px] text-slate font-mono truncate">
                                {operator
                                  .regions
                                  .length >
                                0
                                  ? operator.regions
                                      .slice(
                                        0,
                                        3
                                      )
                                      .join(
                                        " · "
                                      )
                                  : "United Kingdom"}
                              </div>
                            )}
                          </div>

                          <ArrowUpRight className="w-5 h-5 text-slate group-hover:text-sky group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
                        </div>

                        <div className="mt-5 pt-4 border-t border-line flex items-center justify-between gap-3">
                          {operator.count >
                          0 ? (
                            <div className="inline-flex items-center gap-2 text-[13px] text-sky font-semibold">
                              <Briefcase className="w-3.5 h-3.5" />

                              {
                                operator.count
                              }{" "}
                              live{" "}
                              {operator.count ===
                              1
                                ? "vacancy"
                                : "vacancies"}
                            </div>
                          ) : featuredEmployer ? (
                            <div className="inline-flex items-center gap-2 text-[13px] text-sky font-semibold">
                              <Building2 className="w-3.5 h-3.5" />
                              View company profile
                            </div>
                          ) : (
                            <div className="text-[13px] text-slate">
                              No live vacancies
                            </div>
                          )}

                          {featuredEmployer && (
                            <div className="text-[10px] uppercase tracking-[0.14em] font-mono text-gold">
                              Priority
                            </div>
                          )}
                        </div>
                      </Link>
                    </motion.div>
                  );
                }
              )}
            </div>
          </>
        )}
      </main>

      <section className="bg-deep border-t border-line">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 text-center">
          <h2 className="font-display text-ice text-3xl md:text-4xl tracking-[-0.022em] font-extrabold leading-tight">
            Are you hiring?{" "}
            <span className="text-sky">
              Build your employer presence here.
            </span>
          </h2>

          <p className="mt-3 text-slate text-[15px] max-w-2xl mx-auto">
            Approved rail employers can publish vacancies and create a
            company profile free of charge. Featured Employer packages add
            priority directory placement and homepage visibility.
          </p>

          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link
              to="/employer-portal/register"
              className="pill pill-primary"
              data-testid="operators-register-cta"
            >
              Create employer account
            </Link>

            <Link
              to="/employers"
              className="pill pill-ghost"
            >
              Employer options
            </Link>

            <Link
              to="/depot-finder"
              className="pill pill-ghost inline-flex items-center gap-1.5"
            >
              <MapPin className="w-3.5 h-3.5" />
              Vacancy map
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}